'use client';

import React, { useState } from 'react';
import { Link2, Plus, Trash2, ExternalLink } from 'lucide-react';
import { Resource } from '@/lib/storage';

interface ResourcePanelProps {
  resources: Resource[];
  onResourcesChange: (resources: Resource[]) => void;
}

export default function ResourcePanel({ resources, onResourcesChange }: ResourcePanelProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [labelVal, setLabelVal] = useState('');
  const [urlVal, setUrlVal] = useState('');
  const [error, setError] = useState('');

  const handleAdd = () => {
    if (!labelVal.trim()) { setError('Label is required'); return; }
    if (!urlVal.trim()) { setError('URL is required'); return; }
    let url = urlVal.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
    const updated = [...resources, { label: labelVal.trim(), url }];
    onResourcesChange(updated);
    setLabelVal('');
    setUrlVal('');
    setError('');
    setShowAdd(false);
  };

  const handleRemove = (idx: number) => {
    const updated = resources.filter((_, i) => i !== idx);
    onResourcesChange(updated);
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Link2 size={15} style={{ color: 'var(--text-muted)' }} />
          <span
            style={{
              fontSize: '11px',
              fontFamily: 'IBM Plex Mono, monospace',
              fontWeight: 600,
              color: 'var(--text-muted)',
              letterSpacing: '0.06em',
            }}
          >
            RESOURCES
          </span>
          <span
            style={{
              fontSize: '11px',
              fontFamily: 'IBM Plex Mono, monospace',
              color: 'var(--text-muted)',
              backgroundColor: 'var(--bg-elevated)',
              padding: '1px 6px',
              borderRadius: '4px',
              border: '1px solid var(--border)',
            }}
          >
            {resources.length}
          </span>
        </div>
        <button
          onClick={() => setShowAdd(v => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid var(--border)',
            backgroundColor: showAdd ? 'var(--accent-glow)' : 'var(--bg-elevated)',
            color: showAdd ? 'var(--accent)' : 'var(--text-secondary)',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 150ms ease',
          }}
        >
          <Plus size={13} />
          Add Link
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div
          style={{
            padding: '14px',
            backgroundColor: 'var(--bg-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            animation: 'fadeIn 150ms ease forwards',
          }}
        >
          <div>
            <label
              htmlFor="resource-label"
              style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '5px' }}
            >
              Label
            </label>
            <input
              id="resource-label"
              type="text"
              value={labelVal}
              onChange={e => setLabelVal(e.target.value)}
              placeholder="e.g. MDN Web Docs"
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--bg-input)',
                color: 'var(--text-primary)',
                fontSize: '13px',
                outline: 'none',
              }}
              onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--accent-border)'; }}
              onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--border)'; }}
            />
          </div>
          <div>
            <label
              htmlFor="resource-url"
              style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '5px' }}
            >
              URL
            </label>
            <input
              id="resource-url"
              type="url"
              value={urlVal}
              onChange={e => setUrlVal(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="https://..."
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--bg-input)',
                color: 'var(--text-primary)',
                fontSize: '13px',
                fontFamily: 'IBM Plex Mono, monospace',
                outline: 'none',
              }}
              onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--accent-border)'; }}
              onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--border)'; }}
            />
          </div>
          {error && (
            <p style={{ fontSize: '12px', color: 'var(--phase5-text)', margin: 0 }}>{error}</p>
          )}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setShowAdd(false); setError(''); setLabelVal(''); setUrlVal(''); }}
              style={{
                padding: '7px 14px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                backgroundColor: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              style={{
                padding: '7px 14px',
                borderRadius: '6px',
                border: '1px solid var(--accent)',
                backgroundColor: 'var(--accent)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'all 150ms ease',
              }}
            >
              Add Resource
            </button>
          </div>
        </div>
      )}

      {/* Resource list */}
      {resources.length === 0 && !showAdd ? (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            fontSize: '13px',
            color: 'var(--text-muted)',
          }}
        >
          No resources added yet — save useful links for this week here
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {resources.map((res, idx) => (
            <div
              key={`resource-${idx}-${res.label}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                backgroundColor: 'var(--bg-elevated)',
                borderRadius: '8px',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <ExternalLink size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <a
                href={res.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  fontSize: '13px',
                  color: 'var(--accent)',
                  textDecoration: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {res.label}
              </a>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  fontFamily: 'IBM Plex Mono, monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '180px',
                }}
              >
                {res.url.replace(/^https?:\/\//, '')}
              </span>
              <button
                onClick={() => handleRemove(idx)}
                aria-label={`Remove ${res.label}`}
                title="Remove this resource"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: '4px',
                  display: 'flex',
                  flexShrink: 0,
                  transition: 'color 150ms ease',
                  borderRadius: '4px',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--phase5-text)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}